import React, { useState, useRef, useEffect } from 'react';
import DashboardTable from './DashboardTable';
import Pagination from '../../../components/UI/TableGrid/Pagination';
import Alert from '../../../components/UI/ConfirmBox/Alert';
import VisitsService from '../../../services/VisitsService';
import PatientChartsService from '../../../services/PatientChartsService';
import FileManagerService from '../../../services/FileManagerService';

const DashboardCountsFullScreen = ({ 
  category, 
  isOpen, 
  onClose, 
  filters,
  Loader,
  Toast
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [initialPosition, setInitialPosition] = useState(null);
  const containerRef = useRef(null);
  const originalPositionRef = useRef(null);
  const [allData, setAllData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalRecords: 0
  });
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [visitToDelete, setVisitToDelete] = useState(null);
  const [sortColumn, setSortColumn] = useState('visit_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  // Set fullscreen when modal opens
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const pos = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      };
      originalPositionRef.current = pos;
      setInitialPosition(pos);
      setIsFullscreen(true);
      setAppliedSearchTerm('');
      setSearchTerm('');
      setTimeout(() => {
        setInitialPosition(null);
      }, 50);
    } else {
      setIsFullscreen(false);
      setInitialPosition(null);
      setAllData([]);
      setTableData([]);
    }
  }, [isOpen]);

  // Fetch when modal opens or when sort/applied search changes (for visit categories)
  useEffect(() => {
    if (isOpen && isFullscreen && ['physicals', 'drug_screen', 'vaccines'].includes(category)) {
      fetchData();
    }
  }, [isOpen, isFullscreen, category, sortColumn, sortDirection, appliedSearchTerm]);

  // Apply pagination when in fullscreen mode
  useEffect(() => {
    if (isFullscreen && allData.length > 0) {
      const start = (pagination.page - 1) * pagination.perPage;
      const end = start + pagination.perPage;
      const paginatedData = allData.slice(start, end);
      setTableData(paginatedData);
    }
  }, [pagination.page, pagination.perPage, allData, isFullscreen]);

  const getVisitCategoryForPayload = (cat) => {
    const map = {
      'physicals': 'Employer Services - Physicals',
      'drug_screen': 'Employer Services - Drug Screens',
      'vaccines': 'Employer Services - Vaccines',
      'missing_claim_numbers': null
    };
    return map[cat] || null;
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const visitCategory = getVisitCategoryForPayload(category);
      if (!visitCategory && category !== 'missing_claim_numbers') {
        setAllData([]);
        setPagination(prev => ({ ...prev, totalRecords: 0, page: 1 }));
        return;
      }

      const payload = {
        ...filters,
        sort_column: sortColumn || 'visit_date',
        sort_direction: sortDirection || 'desc',
        visits_fullscreen: true
      };
      if (visitCategory) {
        payload.visit_category = visitCategory;
      }
      if (appliedSearchTerm && appliedSearchTerm.trim()) {
        payload.patient_name = appliedSearchTerm.trim();
      }

      const response = await VisitsService.getAllVisits(payload);

      let records = [];
      if (response?.visits && Array.isArray(response.visits)) {
        records = response.visits.map(v => ({
          visit_id: v.visit_id || v.id,
          employee: v.employee ?? '-',
          employee_name: v.employee ?? '-',
          type: v.type ?? '-',
          visit_category: v.type ?? '-',
          visit_date: v.visit_date ?? '-',
          visit_status: v.status ?? '-',
          status: v.status ?? '-',
          statusClass: v.statusClass,
          ...v
        }));
      }

      setAllData(records);
      setPagination(prev => ({ ...prev, totalRecords: records.length, page: 1 }));
    } catch (error) {
      console.error(`Error fetching ${category} data:`, error);
      if (Toast && typeof Toast.error === 'function') {
        Toast.error(`Failed to load ${getCategoryTitle(category)} data`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCategoryTitle = (cat) => {
    const titles = {
      'physicals': 'Physicals',
      'drug_screen': 'Drug Screen',
      'vaccines': 'Vaccines',
      'missing_claim_numbers': 'Missing Claim Numbers'
    };
    return titles[cat] || cat;
  };

  const getColumns = (cat) => {
    switch(cat) {
      case 'physicals':
      case 'drug_screen':
      case 'vaccines':
        return ['EMPLOYEE', 'TYPE', 'VISIT DATE', 'STATUS', 'ACTION'];
      case 'missing_claim_numbers':
        return ['EMPLOYEE', 'INCIDENT DATE', 'CLAIM NUMBER', 'STATUS', 'ACTION'];
      default:
        return [];
    }
  };

  const formatTableData = (records, cat) => {
    return records.map(record => {
      if (cat === 'missing_claim_numbers') {
        return {
          id: record.id || record.incident_id,
          employee: record.employee_name || '-',
          incident_date: record.incident_date || '-',
          claim_number: record.claim_number || 'Missing',
          status: record.status || '-',
          ...record
        };
      } else {
        return {
          id: record.visit_id || record.id,
          employee: record.employee_name || '-',
          type: record.visit_category || '-',
          visit_date: record.visit_date || '-',
          status: record.visit_status || record.status || '-',
          ...record
        };
      }
    });
  };

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Action click handler
  const handleActionClick = (row, action) => {
    const visitId = row.visit_id || row.id;
    
    switch (action) {
      case 'delete':
        setVisitToDelete({ id: visitId, row });
        setShowDeleteModal(true);
        break;
      case 'clearance':
        handleDownloadClearanceReport(visitId);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // Delete visit handler with confirmation
  const handleDeleteVisit = async () => {
    if (!visitToDelete) return;
    
    try {
      if (Loader && typeof Loader === 'function') {
        Loader(true);
      }
      await VisitsService.deleteVisits(visitToDelete.id);
      if (Toast && typeof Toast.success === 'function') {
        Toast.success("Visit deleted successfully");
      }
      setShowDeleteModal(false);
      setVisitToDelete(null);
      // Refresh data
      fetchData();
    } catch (error) {
      console.error("Error deleting visit:", error);
      if (Toast && typeof Toast.error === 'function') {
        Toast.error("Failed to delete visit");
      }
    } finally {
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    }
  };

  // Download clearance report handler
  const handleDownloadClearanceReport = async (visitId) => {
    if (!visitId) return;
    
    if (Loader && typeof Loader === 'function') {
      Loader(true);
    }
    
    try {
      const data = await PatientChartsService.getClearanceReportPDF(null, visitId);
      
      // Check if data is empty, null, undefined, or empty string
      const isEmpty = !data || 
                     data === '' || 
                     data === null || 
                     data === undefined ||
                     (typeof data === 'string' && data.trim() === '') ||
                     (typeof data === 'object' && Object.keys(data).length === 0);
      
      if (isEmpty) {
        console.log("Clearance report data is empty:", data);
        if (Toast && typeof Toast.error === 'function') {
          Toast.error("Clearance report is not available");
        }
        if (Loader && typeof Loader === 'function') {
          Loader(false);
        }
        return;
      }
      
      const pdfData = {
        file_content: data,
        original_name: "clearance-report",
        mime_type: "application/pdf",
      };
      
      FileManagerService.printPdf(pdfData);
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    } catch (error) {
      console.error("Error downloading clearance report:", error);
      if (Toast && typeof Toast.error === 'function') {
        Toast.error("Clearance report is not available");
      }
      if (Loader && typeof Loader === 'function') {
        Loader(false);
      }
    }
  };

  const handleClose = () => {
    if (originalPositionRef.current) {
      setInitialPosition(originalPositionRef.current);
      setTimeout(() => {
        setIsFullscreen(false);
        setInitialPosition(null);
        originalPositionRef.current = null;
        onClose();
      }, 300);
    } else {
      setIsFullscreen(false);
      setInitialPosition(null);
      onClose();
    }
  };

  const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 4L4 12M4 4L12 12"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      className="tblviewcard flip fullscreen-card"
      style={{
        position: 'fixed',
        top: initialPosition ? `${initialPosition.top}px` : '0',
        left: initialPosition ? `${initialPosition.left}px` : '0',
        transform: initialPosition ? 'none' : 'none',
        width: initialPosition ? `${initialPosition.width}px` : '100vw',
        height: initialPosition ? `${initialPosition.height}px` : '100vh',
        zIndex: 1040,
        backgroundColor: '#fff',
        padding: initialPosition ? '20px' : '20px',
        borderRadius: initialPosition ? '8px' : '0',
        boxShadow: initialPosition ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <div className="card mb-0 border-0 shadow-none" style={{ height: '100%', margin: 0 }}>
        <div className="card-header sh_accordionheader">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="text-md font-weight-bold mb-0">{getCategoryTitle(category)}</h6>
            <div>
              <a
                className="btn sh_btniconnew"
                style={{ cursor: 'pointer' }}
                onClick={handleClose}
                title="Close"
              >
                <CloseIcon />
              </a>
            </div>
          </div>
        </div>
        <div className="card-body px-0 pt-0 mt-2 pb-2" style={{ height: 'calc(100% - 60px)', overflow: 'auto' }}>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {['physicals', 'drug_screen', 'vaccines'].includes(category) && (
                <div className="px-3 pt-2 pb-2 d-flex align-items-center justify-content-end" style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <div className="d-flex align-items-center">
                    <div className="mr-2">
                      <input
                        type="text"
                        placeholder="Patient"
                        value={searchTerm}
                        className="form-control"
                        style={{ minWidth: '200px' }}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            const trimmed = searchTerm.trim();
                            setSearchTerm(trimmed);
                            setAppliedSearchTerm(trimmed);
                            setPagination(prev => ({ ...prev, page: 1 }));
                          }
                        }}
                      />
                    </div>
                    <div className="addAction">
                      <div className="btn-group">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            const trimmed = searchTerm.trim();
                            setSearchTerm(trimmed);
                            setAppliedSearchTerm(trimmed);
                            setPagination(prev => ({ ...prev, page: 1 }));
                          }}
                        >
                          Search
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setSearchTerm('');
                            setAppliedSearchTerm('');
                            setPagination(prev => ({ ...prev, page: 1 }));
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <DashboardTable
                columns={getColumns(category)}
                data={formatTableData(tableData, category)}
                onActionClick={handleActionClick}
                sortable={['physicals', 'drug_screen', 'vaccines'].includes(category)}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                actionIcons={['physicals', 'drug_screen', 'vaccines'].includes(category) ? [
                  // {
                  //   svg: (
                  //     <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  //       <path d="M12.3573 7.6416L7.64062 12.3583" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  //       <path d="M12.3573 12.3583L7.64062 7.6416" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  //       <path fillRule="evenodd" clipRule="evenodd" d="M10 17.5V17.5C5.8575 17.5 2.5 14.1425 2.5 10V10C2.5 5.8575 5.8575 2.5 10 2.5V2.5C14.1425 2.5 17.5 5.8575 17.5 10V10C17.5 14.1425 14.1425 17.5 10 17.5Z" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  //     </svg>
                  //   ),
                  //   title: 'Delete',
                  //   action: 'delete'
                  // },
                  {
                    svg: (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.3391 10.2552H11.7148" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.3387 8.63062V10.2553" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M6.67188 10.2552C7.3157 9.07283 8.55435 8.33691 9.90068 8.33691C11.247 8.33691 12.4857 9.07283 13.1295 10.2552" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M6.25391 13.339H7.08759" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.58984 13.339H10.4235" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12.9219 13.339H13.7556" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M6.25278 2.50122V5.00226" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.7567 2.50122V5.00226" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <rect x="2.5" y="3.75171" width="15.0062" height="13.7557" rx="3" stroke="#1D1917" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ),
                    title: 'Download Clearance Report',
                    action: 'clearance'
                  }
                ] : []}
              />
              {isFullscreen && allData.length > 0 && pagination.totalRecords > pagination.perPage && (
                <div className="px-3 pb-2 pt-2" style={{ borderTop: '1px solid #e0e0e0' }}>
                  <Pagination
                    pagination={pagination}
                    fetchTableRecords={handlePaginationChange}
                    tableRows={{ data: tableData }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Alert
          type="delete"
          contentTitle="Delete Visit"
          content="Are you sure you want to delete this visit?"
          yesButtonName="Delete"
          noButtonName="Cancel"
          onClose={() => {
            setShowDeleteModal(false);
            setVisitToDelete(null);
          }}
          onDelete={handleDeleteVisit}
        />
      )}
    </div>
  );
};

export default DashboardCountsFullScreen;

